import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import axios from 'lib/axiosConfig'
import {
  Order,
  OrderEventResponse,
  OrderResponse,
  OrdersResponse,
} from 'types/api/order'
import { Refund, ProductIds } from 'types/model/product'
import { Pagination } from 'types/model/common'

const gtinLengths = [8, 11, 12, 13] // Define GTIN lengths to check in case initial GTIN query to /products returns an empty response

export const useOrders = (
  page: number,
  pageSize: number,
  cleared: boolean,
  startDate: string,
  endDate: string,
  queries: { [key: string]: string } | null
) => {
  const ordersPayload = {
    page,
    page_size: pageSize,
    ...(!cleared && {
      start_delivery_date: startDate,
      end_delivery_date: endDate,
    }),
    ...queries,
  }
  const queryKey = ordersPayload
  const {
    isFetching,
    isLoading,
    error,
    data: response,
  } = useQuery(
    ['getOrders', queryKey],
    async () =>
      axios.get<OrdersResponse>(`/v1/orders`, { params: ordersPayload }),
    { keepPreviousData: true }
  )
  const pagination = response
    ? response?.data.meta.pagination
    : ({} as Pagination)

  const orders = (response ? response.data.data : []) as Order[]
  return { orders, pagination, isLoading, isFetching, error }
}

export const useOrder = (orderId: string) => {
  const initOrderData = {
    visibleId: '',
    customerInfo: {
      customerId: '',
      loyaltyCardId: '',
    },
    customerAddress: {
      name: '',
      email: '',
      phone: '',
    },
    payment: {
      type: '',
    },
    parts: [{ placeId: '' }],
  } as Order

  const {
    isLoading,
    error,
    data: response,
  } = useQuery(['getOrderInfo', orderId], async () =>
    axios.get<OrderResponse>(`/v1/orders/${orderId}`)
  )
  const order = { ...initOrderData, ...response?.data.data }
  return { isLoading, error, order }
}

export const useOrderProducts = (products: ProductIds[]) => {
  const queries = useQueries(
    products.map((product) => ({
      queryKey: ['getProduct', product],
      // products/${id} can't use uuid or id, old implementation filters by gtins
      queryFn: async () => {
        if (product.sku) {
          const response = await axios.get(`/v1/products`, {
            params: { 'ext_ids[]': product.sku },
          })
          if (response?.data?.data?.length > 0) {
            return response
          }
        }
        let stringGtin = product.gtin.toString()
        while (stringGtin.length <= 13) {
          if (gtinLengths.includes(stringGtin.length)) {
            /* eslint-disable no-await-in-loop */
            const response = await axios.get(`/v1/products`, {
              /* eslint-enable no-await-in-loop */
              params: { gtins: stringGtin },
            })
            if (response?.data?.data?.length > 0) {
              return response
            }
          }
          stringGtin = `0${stringGtin}`
        }
        return {
          data: { data: [] },
          status: 200,
          statusText: 'OK',
          headers: {},
          config: {},
        }
      },
      enabled: Boolean(products && products.length > 0),
    }))
  )
  const error = queries.some((q) => q.error)
  const isLoading = queries.some((q) => q.isLoading)
  const productInfo =
    !error && !isLoading
      ? queries
          .map((q) => q.data?.data.data)
          .filter((q) => q.length > 0)
          .map((query) => query[0])
      : []
  return { productInfo, isLoading }
}

export const usePostOrderRefund = (orderId: string, partId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: { refunds: Refund[] }) =>
      axios.post(`/v1/orders/${orderId}/parts/${partId}/refunds`, values),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getOrderInfo', orderId])
      },
      onSuccess: () => {
        queryClient.fetchQuery(['getOrderInfo', orderId])
      },
    }
  )
}

export const usePatchOrderStatus = (orderId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    ([values, partId]: [
      {
        status: string
        crc: string
        details: Record<string, never>
      },
      string
    ]) =>
      axios.patch(`/v1/orders/${orderId}/parts/${partId}/set_status`, values),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getOrderInfo', orderId])
      },
      onSuccess: () => {
        queryClient.fetchQuery(['getOrderInfo', orderId])
      },
    }
  )
}

export const useCancelOrder = (orderId: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    (values: Record<string, never>) =>
      axios.patch(`/v1/orders/${orderId}/cancel`, values),
    {
      onMutate: async () => {
        queryClient.cancelQueries(['getOrderInfo', orderId])
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['getOrderInfo', orderId])
      },
    }
  )
}

export const useOrderEvents = (orderId: string, partId: string) => {
  const {
    isLoading,
    error,
    data: response,
  } = useQuery(
    ['getOrderEvents', orderId + partId],
    async () =>
      axios.get<OrderEventResponse>(
        `/v1/orders/${orderId}/parts/${partId}/events`
      ),
    { enabled: Boolean(partId && orderId) }
  )

  // order events chronologically. When setting manual refund, pending and refunded events happen at the same time so order by type
  const events =
    response?.data.data.sort((a, b) => {
      if (
        new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf() !==
        0
      ) {
        return new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf()
      }
      return a.type - b.type
    }) || []
  return { isLoading, error, events }
}

export const usePostOrderNote = (orderId: string, partId: string) => {
  return useMutation((values: { crc: string; message: string }) =>
    axios.post(`/v1/orders/${orderId}/parts/${partId}/comments`, values)
  )
}
